<svelte:options
  customElement={{
    tag: "oc-promo-card-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({ delegateFocus: true }),
    props: {
      mainHeadline: { type: "String", attribute: "main-headline" },
      overline: { type: "String" },
      footerHeadline: { type: "String", attribute: "footer-headline" },
      footerText: { type: "String", attribute: "footer-text" },
      href: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";
  import type { Props } from "./PromoCardV1.types.js";
  import { useSlots } from "../../../common/utils/useSlots.js";

  usePropertyChange();
  const slots = useSlots();

  export let mainHeadline: Props["mainHeadline"] = undefined;
  export let overline: Props["overline"] = undefined;
  export let footerHeadline: Props["footerHeadline"] = undefined;
  export let footerText: Props["footerText"] = undefined;
  export let href: Props["href"] = undefined;

  $: hasHeader = overline || mainHeadline || $slots.logo;
  $: hasFooter = footerHeadline || footerText;

  $: tag = href ? "a" : "div";
  $: tagProps = { href };
</script>

<svelte:element this={tag} {...tagProps} class="promo-card" class:promo-card--interactive={href}>
  <article>
    {#if hasHeader}
      <header class="promo-card__header">
        <div class="promo-card__header-container">
          {#if overline}
            <span class="promo-card__header-container-overline">{overline}</span>
          {/if}
          {#if mainHeadline}
            <span class="promo-card__header-container-main-headline">{mainHeadline}</span>
          {/if}
        </div>
        {#if $slots.logo}
          <slot name="logo" />
        {/if}
      </header>
    {/if}

    <div class="promo-card__body">
      <slot />
      {#if $slots.tags}
        <div class="promo-card__tag-container">
          <slot name="tags" />
        </div>
      {/if}
    </div>

    {#if hasFooter}
      <footer class="promo-card__footer">
        {#if footerHeadline}
          <span class="promo-card__footer-headline">{footerHeadline}</span>
        {/if}
        {#if footerText}
          <span class="promo-card__footer-text">{footerText}</span>
        {/if}
      </footer>
    {/if}
  </article>
</svelte:element>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "../../../common/scss/mixins/mixins";

  :host {
    @include mixins.tap-highlight();
    display: block;
    position: relative;
    container-type: inline-size;
    flex: 1;
  }

  .promo-card {
    @include mixins.focus-styles(tokens.$oc-component-card-border-radius);
    display: block;

    background-color: tokens.$oc-component-card-background-color;
    border-radius: tokens.$oc-component-card-border-radius;
    overflow: hidden;
    color: inherit;
    /*                                                                                 */
    /*                                                     */
    height: 100%;
    text-decoration: none;

    &--interactive {
      cursor: pointer;

      /*          */
      @media (hover: hover) {
        &:hover {
          &::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: inherit;
            pointer-events: none;
            background-color: tokens.$oc-component-card-overlay-hover;
          }
        }
      }

      /*           */
      &:active {
        &::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: inherit;
          pointer-events: none;
          background-color: tokens.$oc-component-card-overlay-active;
        }
      }
    }
  }

  .promo-card__header {
    display: flex;
    flex-direction: row;
    gap: tokens.$oc-component-promo-card-gap-logo;
    padding: tokens.$oc-component-promo-card-padding-x tokens.$oc-component-promo-card-padding-y;
    align-items: center;

    height: calc(5em - 2 * tokens.$oc-component-promo-card-padding-y);

    @include mixins.breakpoint-from-l(false, $containerFirst: true) {
      height: calc(5.5em - 2 * tokens.$oc-component-promo-card-padding-y);
    }

    &-container {
      display: flex;
      flex-direction: column;
      justify-content: center;

      /*                           */
      flex: 1;
      &,
      & > * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &-overline {
        font: tokens.$oc-component-promo-card-overline-font;
      }

      &-main-headline {
        font: tokens.$oc-component-promo-card-s-m-first-headline-font;
        @include mixins.breakpoint-from-l(false, $containerFirst: true) {
          font: tokens.$oc-component-promo-card-l-xl-first-headline-font;
        }
      }
    }
  }

  /*               */
  .promo-card__body {
    position: relative;
    overflow: hidden;
  }
  .promo-card__tag-container {
    bottom: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px;
    position: absolute;
  }

  .promo-card__footer {
    display: flex;
    flex-direction: column;
    min-height: 24px;
    padding: 16px;

    &-headline {
      /*                         */
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;

      font: tokens.$oc-component-promo-card-s-m-second-headline-font;
      @include mixins.breakpoint-from-l(false, $containerFirst: true) {
        font: tokens.$oc-component-promo-card-l-xl-second-headline-font;
      }
    }

    &-text {
      font: tokens.$oc-component-promo-card-text-font;
    }
  }
</style>
